import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useBed } from '../../../context/BedContext';
import { useAuth } from '../../../context/AuthContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS } from '../../../data/nurseAppModels';
import { collection, query, where, getDocs, orderBy, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import EquipmentImageUpload from './EquipmentImageUpload';
import { uploadFile } from '../../../utils/storageUtils';
import { compressImage } from '../../../utils/imageUtils';
import RepairCompletionModal from './RepairCompletionModal';

const EquipmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { equipment, loading: equipmentLoading, recalculateStock, receiveRepair, requestRepairCompletion, changeEquipmentStatus } = useEquipment();
  const { currentTeam } = useTeam();
  const { getAvailableBeds, changeBedStatus } = useBed();
  const { currentUser } = useAuth();

  const [stockHistory, setStockHistory] = useState([]);
  const [inspectionHistory, setInspectionHistory] = useState([]);
  const [borrowHistory, setBorrowHistory] = useState([]);
  const [repairHistory, setRepairHistory] = useState([]);
  const [repairHistoryError, setRepairHistoryError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [savingImages, setSavingImages] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [receiveCondition, setReceiveCondition] = useState('GOOD');
  const [receiveDescription, setReceiveDescription] = useState('');
  const [receiveImages, setReceiveImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [selectedCompletionRepair, setSelectedCompletionRepair] = useState(null);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [newStatus, setNewStatus] = useState(null);
  const [statusChangeData, setStatusChangeData] = useState({
    patientBed: '',
    notes: ''
  });
  const [availableBeds, setAvailableBeds] = useState([]);

  const equipmentData = equipment.find(eq => eq.id === id);

  // Load images
  useEffect(() => {
    if (equipmentData?.images) {
      setImages(Array.isArray(equipmentData.images) ? equipmentData.images : []);
    }
  }, [equipmentData]);

  // Save images to equipment
  const handleImagesChange = async (newImages) => {
    setImages(newImages);
    if (!equipmentData || !id) return;

    try {
      setSavingImages(true);
      const equipmentRef = doc(db, 'equipment', id);
      await updateDoc(equipmentRef, {
        images: newImages.map(img => img.url),
        thumbnail: newImages.length > 0 ? newImages[0].url : null,
        updatedAt: Timestamp.now()
      });
    } catch (err) {
      console.error('Error saving images:', err);
      alert(`เกิดข้อผิดพลาดในการบันทึกรูปภาพ: ${err.message}`);
    } finally {
      setSavingImages(false);
    }
  };

  // Fetch history
  useEffect(() => {
    const fetchHistory = async () => {
      if (!equipmentData) return;

      try {
        setLoading(true);

        // Fetch stock transactions
        const stockQuery = query(
          collection(db, 'equipmentStockTransactions'),
          where('equipmentId', '==', id),
          orderBy('createdAt', 'desc')
        );
        const stockSnapshot = await getDocs(stockQuery);
        const stockData = stockSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStockHistory(stockData);

        // Fetch inspections
        const inspectionQuery = query(
          collection(db, 'equipmentInspections'),
          where('items', 'array-contains-any', [{ equipmentId: id }]),
          orderBy('date', 'desc')
        );
        const inspectionSnapshot = await getDocs(inspectionQuery);
        const inspectionData = inspectionSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInspectionHistory(inspectionData);

        // Fetch borrow history (for large items)
        if (equipmentData.type === EQUIPMENT_TYPES.LARGE_ITEM) {
          const borrowQuery = query(
            collection(db, 'equipmentBorrows'),
            where('equipmentId', '==', id),
            orderBy('createdAt', 'desc')
          );
          const borrowSnapshot = await getDocs(borrowQuery);
          const borrowData = borrowSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setBorrowHistory(borrowData);

          // Fetch repair history (for large items)
          try {
            const repairQuery = query(
              collection(db, 'equipmentRepairs'),
              where('equipmentId', '==', id),
              orderBy('createdAt', 'desc')
            );
            const repairSnapshot = await getDocs(repairQuery);
            const repairData = repairSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            console.log('Repair history fetched:', repairData.length, 'items');
            setRepairHistory(repairData);
            setRepairHistoryError(null);
          } catch (repairErr) {
            console.error('Error fetching repair history:', repairErr);
            // Fallback: query without orderBy
            if (repairErr.code === 'failed-precondition') {
              console.warn('⚠️ Firestore index missing for equipmentRepairs (equipmentId + createdAt). Using fallback query.');
              setRepairHistoryError('index_missing');
              try {
                const fallbackRepairQuery = query(
                  collection(db, 'equipmentRepairs'),
                  where('equipmentId', '==', id)
                );
                const fallbackRepairSnapshot = await getDocs(fallbackRepairQuery);
                const fallbackRepairData = fallbackRepairSnapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));
                // Sort in memory
                fallbackRepairData.sort((a, b) => {
                  const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
                  const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
                  return dateB - dateA;
                });
                console.log('Repair history fetched (fallback):', fallbackRepairData.length, 'items');
                setRepairHistory(fallbackRepairData);
              } catch (fallbackErr) {
                console.error('Error fetching repair history (fallback):', fallbackErr);
                setRepairHistory([]);
                setRepairHistoryError('error');
              }
            } else {
              setRepairHistory([]);
              setRepairHistoryError('error');
            }
          }
        }
      } catch (err) {
        console.error('Error fetching history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [equipmentData, id]);

  if (equipmentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!equipmentData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">ไม่พบอุปกรณ์</p>
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            กลับไปหน้ารายการ
          </button>
        </div>
      </div>
    );
  }

  const isSmallItem = equipmentData.type === EQUIPMENT_TYPES.SMALL_ITEM;
  const statusLabels = {
    [EQUIPMENT_STATUS.PENDING_APPROVAL]: 'รออนุมัติ',
    [EQUIPMENT_STATUS.REJECTED]: 'ถูกปฏิเสธ',
    [EQUIPMENT_STATUS.AVAILABLE]: 'ว่างพร้อมใช้งาน',
    [EQUIPMENT_STATUS.IN_USE]: 'กำลังใช้งานอยู่',
    [EQUIPMENT_STATUS.BORROWED]: 'ถูกยืม',
    [EQUIPMENT_STATUS.MAINTENANCE]: 'บำรุงรักษา',
    [EQUIPMENT_STATUS.REPAIR]: 'อยู่ระหว่างส่งซ่อม',
    [EQUIPMENT_STATUS.UNAVAILABLE]: 'ไม่พร้อมใช้งาน',
    [EQUIPMENT_STATUS.DAMAGED]: 'เสียหาย',
    [EQUIPMENT_STATUS.LOST]: 'สูญหาย'
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case EQUIPMENT_STATUS.PENDING_APPROVAL:
        return <span className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded">⏳ รออนุมัติ</span>;
      case EQUIPMENT_STATUS.REJECTED:
        return <span className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded">❌ ถูกปฏิเสธ</span>;
      case EQUIPMENT_STATUS.AVAILABLE:
        return <span className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded">✅ ว่างพร้อมใช้งาน</span>;
      case EQUIPMENT_STATUS.IN_USE:
        return <span className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded">🔄 กำลังใช้งานอยู่</span>;
      case EQUIPMENT_STATUS.BORROWED:
        return <span className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded">📦 ถูกยืม</span>;
      case EQUIPMENT_STATUS.REPAIR:
        return <span className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded">🔧 อยู่ระหว่างส่งซ่อม</span>;
      default:
        return <span className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded">{statusLabels[status] || status}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{equipmentData.name}</h1>
              <p className="text-gray-600">{equipmentData.code}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 text-sm rounded ${
                isSmallItem ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
              }`}>
                {isSmallItem ? 'ชิ้นเล็ก' : 'ชิ้นใหญ่'}
              </span>
              {getStatusBadge(equipmentData.status)}
            </div>
          </div>
          {equipmentData.status === EQUIPMENT_STATUS.REJECTED && equipmentData.rejectionReason && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800 mb-1">เหตุผลที่ปฏิเสธ:</p>
              <p className="text-sm text-red-700">{equipmentData.rejectionReason}</p>
            </div>
          )}
        </div>

        {/* Equipment Images */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">📷 รูปภาพอุปกรณ์</h2>
          <EquipmentImageUpload
            images={images}
            onChange={handleImagesChange}
            equipmentId={id}
            teamId={currentTeam?.id}
            userPlan={currentUser?.plan || 'FREE'}
          />
          {savingImages && (
            <div className="mt-4 text-center text-sm text-gray-600">
              กำลังบันทึกรูปภาพ...
            </div>
          )}
        </div>

        {/* Equipment Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">ข้อมูลอุปกรณ์</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">หมวดหมู่</p>
              <p className="font-medium text-gray-800">{equipmentData.category || '-'}</p>
            </div>
            {equipmentData.subCategory && (
              <div>
                <p className="text-sm text-gray-600">หมวดหมู่ย่อย</p>
                <p className="font-medium text-gray-800">{equipmentData.subCategory}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">หน่วย</p>
              <p className="font-medium text-gray-800">{equipmentData.unit || '-'}</p>
            </div>
            {isSmallItem ? (
              <>
                <div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Stock ปัจจุบัน</p>
                      <p className="font-medium text-gray-800 text-xl">
                        {equipmentData.currentStock || 0} {equipmentData.unit}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        if (window.confirm('ต้องการคำนวณ Stock ใหม่จาก Approved Transactions ทั้งหมดหรือไม่?')) {
                          try {
                            const result = await recalculateStock(id);
                            if (result.success) {
                              alert(`คำนวณ Stock สำเร็จ: ${result.newStock} ${equipmentData.unit}`);
                            } else {
                              alert(`เกิดข้อผิดพลาด: ${result.error}`);
                            }
                          } catch (err) {
                            alert(`เกิดข้อผิดพลาด: ${err.message}`);
                          }
                        }
                      }}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      title="คำนวณ Stock ใหม่จาก Approved Transactions"
                    >
                      🔄 คำนวณใหม่
                    </button>
                  </div>
                </div>
                {equipmentData.minStock && (
                  <div>
                    <p className="text-sm text-gray-600">Stock ต่ำสุด</p>
                    <p className="font-medium text-gray-800">
                      {equipmentData.minStock} {equipmentData.unit}
                    </p>
                  </div>
                )}
                {equipmentData.maxStock && (
                  <div>
                    <p className="text-sm text-gray-600">Stock สูงสุด</p>
                    <p className="font-medium text-gray-800">
                      {equipmentData.maxStock} {equipmentData.unit}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm text-gray-600">สถานะ</p>
                  <p className="font-medium text-gray-800">
                    {statusLabels[equipmentData.status] || equipmentData.status}
                  </p>
                </div>
                {equipmentData.barcode && (
                  <div>
                    <p className="text-sm text-gray-600">Barcode</p>
                    <p className="font-medium text-gray-800">{equipmentData.barcode}</p>
                  </div>
                )}
                {equipmentData.serialNumber && (
                  <div>
                    <p className="text-sm text-gray-600">Serial Number</p>
                    <p className="font-medium text-gray-800">{equipmentData.serialNumber}</p>
                  </div>
                )}
              </>
            )}
            {equipmentData.currentLocation && (
              <div>
                <p className="text-sm text-gray-600">ตำแหน่งเก็บอุปกรณ์</p>
                <p className="font-medium text-gray-800">{equipmentData.currentLocation}</p>
              </div>
            )}
            {equipmentData.department && (
              <div>
                <p className="text-sm text-gray-600">แผนก</p>
                <p className="font-medium text-gray-800">{equipmentData.department}</p>
              </div>
            )}
            {equipmentData.ward && (
              <div>
                <p className="text-sm text-gray-600">หอผู้ป่วย</p>
                <p className="font-medium text-gray-800">{equipmentData.ward}</p>
              </div>
            )}
          </div>
        </div>

        {/* Stock History (Small Items) */}
        {isSmallItem && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">📊 ประวัติ Stock</h2>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : stockHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>ไม่มีประวัติ Stock</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stockHistory.slice(0, 10).map(tx => {
                  const date = tx.createdAt?.toDate ? tx.createdAt.toDate() : new Date();
                  return (
                    <div key={tx.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-800">
                            {tx.type === 'STOCK_IN' ? '📥' : '📤'} {tx.type === 'STOCK_IN' ? 'นำเข้า' : 'นำออก'}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            จำนวน: {tx.quantity} {equipmentData.unit}
                          </p>
                          <p className="text-sm text-gray-600">
                            โดย: {tx.performedByName} | {date.toLocaleDateString('th-TH')} {date.toLocaleTimeString('th-TH')}
                          </p>
                          {tx.notes && (
                            <p className="text-sm text-gray-500 mt-1">หมายเหตุ: {tx.notes}</p>
                          )}
                        </div>
                        <span className={`px-2 py-1 text-xs rounded ${
                          tx.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                          tx.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {tx.status === 'APPROVED' ? 'อนุมัติแล้ว' :
                           tx.status === 'PENDING' ? 'รออนุมัติ' : tx.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Inspection History */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">🔍 ประวัติการตรวจสอบ</h2>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : inspectionHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>ยังไม่มีการตรวจสอบ</p>
            </div>
          ) : (
            <div className="space-y-3">
              {inspectionHistory.slice(0, 10).map(inspection => {
                const date = inspection.date?.toDate ? inspection.date.toDate() : new Date();
                const itemData = inspection.items?.find(item => item.equipmentId === id);
                return (
                  <div key={inspection.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-800">
                          {inspection.round === 'MORNING_BEFORE' ? 'เช้า - ก่อนเข้าเวร' :
                           inspection.round === 'MORNING_AFTER' ? 'เช้า - หลังเข้าเวร' :
                           inspection.round === 'AFTERNOON_BEFORE' ? 'บ่าย - ก่อนเข้าเวร' :
                           inspection.round === 'AFTERNOON_AFTER' ? 'บ่าย - หลังเข้าเวร' :
                           inspection.round === 'NIGHT_BEFORE' ? 'ดึก - ก่อนเข้าเวร' :
                           'ดึก - หลังเข้าเวร'}
                        </p>
                        {isSmallItem && itemData && (
                          <p className="text-sm text-gray-600 mt-1">
                            คงเหลือ: {itemData.availableQuantity || 0} | 
                            เสียหาย: {itemData.damagedQuantity || 0} | 
                            สูญหาย: {itemData.lostQuantity || 0} {equipmentData.unit}
                          </p>
                        )}
                        {!isSmallItem && itemData && (
                          <p className="text-sm text-gray-600 mt-1">
                            สถานะ: {statusLabels[itemData.status] || itemData.status}
                            {itemData.condition && ` | สภาพ: ${itemData.condition}`}
                          </p>
                        )}
                        <p className="text-sm text-gray-600">
                          โดย: {inspection.inspectedByName} | {date.toLocaleDateString('th-TH')}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Borrow History (Large Items) */}
        {!isSmallItem && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">📦 ประวัติการยืม</h2>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : borrowHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>ยังไม่มีการยืม</p>
              </div>
            ) : (
              <div className="space-y-3">
                {borrowHistory.slice(0, 10).map(borrow => {
                  const date = borrow.createdAt?.toDate ? borrow.createdAt.toDate() : new Date();
                  return (
                    <div key={borrow.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-800">
                            {borrow.type === 'BORROW' ? '📤 ยืม' : '📥 คืน'}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            โดย: {borrow.borrowedByName || borrow.returnedByName} | {date.toLocaleDateString('th-TH')}
                          </p>
                          {borrow.notes && (
                            <p className="text-sm text-gray-500 mt-1">หมายเหตุ: {borrow.notes}</p>
                          )}
                        </div>
                        <span className={`px-2 py-1 text-xs rounded ${
                          borrow.status === 'APPROVED' || borrow.status === 'COMPLETED' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {borrow.status === 'APPROVED' ? 'อนุมัติแล้ว' :
                           borrow.status === 'COMPLETED' ? 'คืนแล้ว' :
                           borrow.status === 'PENDING' ? 'รออนุมัติ' : borrow.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Repair History (Large Items) */}
        {!isSmallItem && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">🔧 ประวัติการซ่อม</h2>
            {repairHistoryError === 'index_missing' && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ ยังไม่มี Index ใน Firebase กรุณา deploy index หรือคลิกลิงก์ใน Console เพื่อสร้าง index
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Index ที่ต้องสร้าง: equipmentRepairs (equipmentId + createdAt)
                </p>
                <a
                  href="https://console.firebase.google.com/project/_/firestore/indexes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 underline mt-1 inline-block"
                >
                  ไปที่ Firebase Console →
                </a>
              </div>
            )}
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : repairHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>ยังไม่มีการซ่อม</p>
              </div>
            ) : (
              <div className="space-y-3">
                {repairHistory.map(repair => {
                  const createdAt = repair.createdAt?.toDate ? repair.createdAt.toDate() : new Date();
                  const completedAt = repair.completedAt?.toDate ? repair.completedAt.toDate() : null;
                  const approvedAt = repair.approvedAt?.toDate ? repair.approvedAt.toDate() : null;
                  const receiveApprovedAt = repair.receiveApprovedAt?.toDate ? repair.receiveApprovedAt.toDate() : null;
                  
                  const getStatusBadge = () => {
                    if (repair.receiveStatus === 'APPROVED') {
                      return { bg: 'bg-green-100', text: 'text-green-700', label: '✅ รับกลับแล้ว' };
                    } else if (repair.receiveStatus === 'PENDING') {
                      return { bg: 'bg-blue-100', text: 'text-blue-700', label: '📥 รออนุมัติรับกลับ' };
                    } else if (repair.receiveStatus === 'REJECTED') {
                      return { bg: 'bg-orange-100', text: 'text-orange-700', label: '❌ รับกลับถูกปฏิเสธ' };
                    } else if (repair.status === 'COMPLETED') {
                      return { bg: 'bg-gray-100', text: 'text-gray-700', label: '✅ ซ่อมเสร็จแล้ว' };
                    } else if (repair.status === 'IN_PROGRESS') {
                      return { bg: 'bg-blue-100', text: 'text-blue-700', label: '🔧 กำลังซ่อม' };
                    } else if (repair.status === 'APPROVED') {
                      return { bg: 'bg-orange-100', text: 'text-orange-700', label: '🔧 อยู่ระหว่างส่งซ่อม' };
                    } else if (repair.status === 'REJECTED') {
                      return { bg: 'bg-red-100', text: 'text-red-700', label: '❌ ถูกปฏิเสธ' };
                    } else {
                      return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '⏳ รออนุมัติ' };
                    }
                  };

                  const statusBadge = getStatusBadge();

                  return (
                    <div key={repair.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">
                            🔧 ส่งซ่อม
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            โดย: {repair.reportedByName} | {createdAt.toLocaleDateString('th-TH')}
                          </p>
                          {repair.problemDescription && (
                            <p className="text-sm text-gray-500 mt-1">
                              ปัญหา: {repair.problemDescription}
                            </p>
                          )}
                          {repair.problemType && (
                            <p className="text-xs text-gray-500 mt-1">
                              ประเภท: {
                                repair.problemType === 'NOT_WORKING' ? 'ไม่ทำงาน' :
                                repair.problemType === 'DAMAGED' ? 'เสียหาย' :
                                repair.problemType === 'MAINTENANCE' ? 'บำรุงรักษา' : 'อื่นๆ'
                              }
                            </p>
                          )}
                          {approvedAt && (
                            <p className="text-xs text-gray-500 mt-1">
                              อนุมัติเมื่อ: {approvedAt.toLocaleDateString('th-TH')} โดย {repair.approvedByName}
                            </p>
                          )}
                          {completedAt && (
                            <p className="text-xs text-gray-500 mt-1">
                              ซ่อมเสร็จเมื่อ: {completedAt.toLocaleDateString('th-TH')}
                            </p>
                          )}
                          {receiveApprovedAt && (
                            <p className="text-xs text-gray-500 mt-1">
                              รับกลับเมื่อ: {receiveApprovedAt.toLocaleDateString('th-TH')} โดย {repair.receiveApprovedByName}
                            </p>
                          )}
                          {repair.receiveRejectionReason && (
                            <p className="text-xs text-red-600 mt-1">
                              เหตุผลที่ปฏิเสธ: {repair.receiveRejectionReason}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          <span className={`px-2 py-1 text-xs rounded ${statusBadge.bg} ${statusBadge.text}`}>
                            {statusBadge.label}
                          </span>
                          {/* Show completion request button if status is APPROVED or IN_PROGRESS */}
                          {(repair.status === 'APPROVED' || repair.status === 'IN_PROGRESS') && (
                            <>
                              {repair.completionRequestStatus === 'PENDING' ? (
                                <span className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-xs font-medium whitespace-nowrap">
                                  ⏳ รออนุมัติซ่อมเสร็จ
                                </span>
                              ) : !repair.completionRequestStatus ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('Button clicked, repair:', repair);
                                    setSelectedCompletionRepair(repair);
                                    setShowCompletionModal(true);
                                  }}
                                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium whitespace-nowrap cursor-pointer"
                                >
                                  ✅ ซ่อมเสร็จแล้ว
                                </button>
                              ) : null}
                            </>
                          )}
                          {/* Show receive button if repair is completed (has completedAt OR status is COMPLETED) and not yet received */}
                          {(completedAt || repair.status === 'COMPLETED') && !repair.receiveStatus && (
                            <button
                              onClick={() => {
                                setSelectedRepair(repair);
                                setShowReceiveModal(true);
                              }}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium whitespace-nowrap"
                            >
                              📥 รับกลับ
                            </button>
                          )}
                          {repair.receiveStatus === 'REJECTED' && (
                            <button
                              onClick={() => {
                                setSelectedRepair(repair);
                                setShowReceiveModal(true);
                              }}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium whitespace-nowrap"
                            >
                              📥 รับกลับใหม่
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isSmallItem && (
            <Link
              to="/nurse/equipment/stock"
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
            >
              <div className="text-4xl mb-2">📥</div>
              <h3 className="font-semibold text-gray-800">นำเข้า Stock</h3>
            </Link>
          )}
          <Link
            to="/nurse/equipment/inspection"
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition text-center"
          >
            <div className="text-4xl mb-2">🔍</div>
            <h3 className="font-semibold text-gray-800">ตรวจสอบ</h3>
          </Link>
        </div>
      </div>

      {/* Receive Repair Modal */}
      {showReceiveModal && selectedRepair && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">รับกลับอุปกรณ์จากการซ่อม</h3>
            
            {/* Equipment Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🩺</span>
                <div>
                  <h4 className="font-semibold text-gray-800">{equipmentData?.name}</h4>
                  <p className="text-sm text-gray-600">{equipmentData?.code}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Condition */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  สภาพอุปกรณ์ <span className="text-red-500">*</span>
                </label>
                <select
                  value={receiveCondition}
                  onChange={(e) => setReceiveCondition(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="GOOD">✅ ดี (พร้อมใช้งาน)</option>
                  <option value="DAMAGED">⚠️ เสียหาย</option>
                  <option value="NEEDS_REPAIR">🔧 ต้องซ่อมอีก</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รายละเอียดเพิ่มเติม
                </label>
                <textarea
                  value={receiveDescription}
                  onChange={(e) => setReceiveDescription(e.target.value)}
                  rows={4}
                  placeholder="ระบุรายละเอียดสภาพอุปกรณ์..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รูปภาพ (ถ้ามี)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    if (files.length === 0) return;

                    // Check plan-based limit
                    const userPlan = currentUser?.plan || 'FREE';
                    const maxImages = userPlan === 'PRO' ? 3 : 1;
                    const remainingSlots = maxImages - receiveImages.length;

                    if (remainingSlots <= 0) {
                      alert(`ถึงขีดจำกัดการอัพโหลดรูปภาพแล้ว (${maxImages} รูป${userPlan === 'FREE' ? ' - สมัคร Pro Plan เพื่ออัพโหลดได้สูงสุด 3 รูป' : ''})`);
                      return;
                    }

                    const filesToUpload = files.slice(0, remainingSlots);
                    if (files.length > remainingSlots) {
                      alert(`สามารถอัพโหลดได้อีก ${remainingSlots} รูปเท่านั้น (สูงสุด ${maxImages} รูป)`);
                    }

                    try {
                      setUploadingImages(true);
                      const uploadedUrls = [];

                      for (const file of filesToUpload) {
                        const compressedFile = await compressImage(file, {
                          maxWidth: 1920,
                          maxHeight: 1920,
                          quality: 0.8
                        });

                        const fileName = `equipment-receive-${Date.now()}-${Math.random().toString(36).substring(7)}`;
                        const path = `equipment/${currentTeam.id}/${fileName}`;
                        const url = await uploadFile(compressedFile, path);
                        uploadedUrls.push(url);
                      }

                      setReceiveImages([...receiveImages, ...uploadedUrls]);
                    } catch (err) {
                      console.error('Error uploading images:', err);
                      alert('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
                    } finally {
                      setUploadingImages(false);
                    }
                  }}
                  disabled={uploadingImages}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {receiveImages.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {receiveImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img
                          src={img}
                          alt={`Receive ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => setReceiveImages(receiveImages.filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => {
                  setShowReceiveModal(false);
                  setSelectedRepair(null);
                  setReceiveCondition('GOOD');
                  setReceiveDescription('');
                  setReceiveImages([]);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                ยกเลิก
              </button>
              <button
                onClick={async () => {
                  if (!selectedRepair) return;

                  try {
                    setIsSubmitting(true);
                    const result = await receiveRepair(selectedRepair.id, {
                      condition: receiveCondition,
                      description: receiveDescription.trim(),
                      images: receiveImages
                    });

                    if (result.success) {
                      alert('ส่งคำขอรับกลับอุปกรณ์สำเร็จ รอการอนุมัติจากหัวหน้าทีม');
                      setShowReceiveModal(false);
                      setSelectedRepair(null);
                      setReceiveCondition('GOOD');
                      setReceiveDescription('');
                      setReceiveImages([]);
                      // Reload page to refresh repair history
                      window.location.reload();
                    } else {
                      alert(`เกิดข้อผิดพลาด: ${result.error}`);
                    }
                  } catch (err) {
                    console.error('Error receiving repair:', err);
                    alert(`เกิดข้อผิดพลาด: ${err.message}`);
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                disabled={isSubmitting || uploadingImages}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isSubmitting ? 'กำลังส่ง...' : 'ส่งคำขอรับกลับ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Completion Request Modal */}
      <RepairCompletionModal
        isOpen={showCompletionModal}
        onClose={() => {
          setShowCompletionModal(false);
          setSelectedCompletionRepair(null);
        }}
        repair={selectedCompletionRepair}
        equipmentData={equipmentData}
        onSuccess={() => {
          window.location.reload();
        }}
      />

      {/* Status Change Modal */}
      {showStatusChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {newStatus === EQUIPMENT_STATUS.IN_USE ? 'เปลี่ยนเป็นกำลังใช้งาน' : 'เปลี่ยนเป็นว่าง'}
            </h3>
            
            {newStatus === EQUIPMENT_STATUS.IN_USE && (
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    เตียงผู้ป่วย *
                  </label>
                  {availableBeds.length === 0 ? (
                    <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50">
                      <p className="text-sm text-gray-500">ไม่มีเตียงว่าง</p>
                    </div>
                  ) : (
                    <select
                      value={statusChangeData.patientBed}
                      onChange={(e) => setStatusChangeData({ ...statusChangeData, patientBed: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">-- เลือกเตียง --</option>
                      {availableBeds.map(bed => (
                        <option key={bed.id} value={bed.id}>
                          {bed.bedName} ({bed.ward})
                        </option>
                      ))}
                    </select>
                  )}
                  {availableBeds.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      💡 กรุณาเพิ่มเตียงที่ <a href="/nurse/teams/beds" className="text-blue-600 hover:underline">จัดการเตียงผู้ป่วย</a>
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    หมายเหตุ
                  </label>
                  <textarea
                    value={statusChangeData.notes}
                    onChange={(e) => setStatusChangeData({ ...statusChangeData, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="หมายเหตุเพิ่มเติม (ไม่บังคับ)"
                  />
                </div>
              </div>
            )}

            {newStatus === EQUIPMENT_STATUS.AVAILABLE && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  คุณต้องการเปลี่ยนสถานะเป็น "ว่างพร้อมใช้งาน" หรือไม่?
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  if (newStatus === EQUIPMENT_STATUS.IN_USE && !statusChangeData.patientBed) {
                    alert('กรุณาเลือกเตียงผู้ป่วย');
                    return;
                  }

                  try {
                    const result = await changeEquipmentStatus(id, newStatus, statusChangeData);
                    if (result.success) {
                      // อัพเดท bed status
                      if (newStatus === EQUIPMENT_STATUS.IN_USE && statusChangeData.patientBed) {
                        try {
                          await changeBedStatus(statusChangeData.patientBed, 'IN_USE');
                        } catch (bedError) {
                          console.error('Error updating bed status:', bedError);
                        }
                      } else if (newStatus === EQUIPMENT_STATUS.AVAILABLE && equipmentData?.inUsePatientBed) {
                        // Check if bed has other equipment in use
                        try {
                          const equipmentQuery = query(
                            collection(db, 'equipment'),
                            where('teamId', '==', currentTeam?.id),
                            where('status', '==', EQUIPMENT_STATUS.IN_USE),
                            where('inUsePatientBed', '==', equipmentData.inUsePatientBed)
                          );
                          const equipmentSnapshot = await getDocs(equipmentQuery);
                          
                          // If no other equipment is using this bed, set bed to AVAILABLE
                          if (equipmentSnapshot.empty) {
                            await changeBedStatus(equipmentData.inUsePatientBed, 'AVAILABLE');
                          }
                        } catch (bedError) {
                          console.error('Error updating bed status:', bedError);
                        }
                      }
                      
                      alert(`เปลี่ยนสถานะสำเร็จ`);
                      setShowStatusChangeModal(false);
                      setStatusChangeData({ patientBed: '', notes: '' });
                    } else {
                      alert(`เกิดข้อผิดพลาด: ${result.error}`);
                    }
                  } catch (err) {
                    alert(`เกิดข้อผิดพลาด: ${err.message}`);
                  }
                }}
                className={`flex-1 px-4 py-2 rounded-lg text-white transition ${
                  newStatus === EQUIPMENT_STATUS.IN_USE
                    ? 'bg-purple-600 hover:bg-purple-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                ยืนยัน
              </button>
              <button
                onClick={() => {
                  setShowStatusChangeModal(false);
                  setStatusChangeData({ patientBed: '', notes: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentDetailPage;

